/**
 * Timezone Utilities
 *
 * Functions for handling timezone display and conversion
 * in the SemanticDatePicker component.
 */

/**
 * Get the timezone abbreviation for a given IANA timezone string.
 * Correctly handles DST transitions (EST/EDT, PST/PDT, etc.).
 *
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @param date - Date to get abbreviation for (affects DST)
 * @returns Timezone abbreviation (e.g., 'EST', 'EDT', 'PST')
 *
 * @example
 * getTimezoneAbbreviation('America/New_York', new Date('2025-01-15'))
 * // Returns 'EST' (winter)
 *
 * getTimezoneAbbreviation('America/New_York', new Date('2025-07-15'))
 * // Returns 'EDT' (summer/DST)
 */
export function getTimezoneAbbreviation(
  timezone: string | undefined,
  date: Date = new Date()
): string {
  if (!timezone) {
    // Get browser's current timezone abbreviation
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
      }).formatToParts(date)
      return parts.find((p) => p.type === 'timeZoneName')?.value || ''
    } catch {
      return ''
    }
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find((p) => p.type === 'timeZoneName')?.value || timezone
  } catch {
    // Invalid timezone string, return as-is
    return timezone
  }
}

/**
 * Get the browser's detected timezone as an IANA string.
 *
 * @returns IANA timezone string (e.g., 'America/New_York', 'Europe/London')
 *
 * @example
 * getBrowserTimezone()
 * // Returns 'America/New_York' (if user is in Eastern US)
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Common US timezone options for dropdown selectors.
 * Curated list for US-first customer base.
 */
export const US_TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
] as const
